import React, { useState, useCallback, useRef, useEffect, useMemo, useLayoutEffect } from "react";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";
import MT_ColumnHeader from "./main_table_parts/MT_ColumnHeader";
import MT_Datatable, { TableItem, TableDataRow, TableSubfolder } from "./main_table_parts/MT_Datatable";
import MT_Pagination from "./main_table_parts/MT_Pagination";
import CellEditor from "./cell-editors";
import { TooltipProvider } from "@/components/ui/tooltip";
// Note: TableColumnSelector provides column options per item type via getMetadataColumnsForItemType
import { useFieldOptions } from "@/hooks/useFieldOptions";
import { FieldMetadata, EditableType, LinkedDataSourceType } from "@/lib/dataSources/types";
import { objectsDataSource } from "@/lib/dataSources/sources/objects";
import { resolveFieldDisplayValue, mapFieldTypeToLinkedDataSource } from "@/lib/utils/fieldValueResolver";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";

/**
 * Field metadata for standard table columns
 * Maps display column keys (like "Name", "Owner") to field metadata
 * Uses DataSource fields as the source of truth, with key mapping for display names
 * This is a fallback - item-type-specific fieldMetadata should be passed as prop
 */
const getDefaultFieldMetadata = (): Record<string, FieldMetadata> => {
  const dsFields = objectsDataSource.fields;
  
  // Map display column names to DataSource field keys
  // Includes common mappings for all item types
  return {
    // Common columns
    'Name': dsFields.name,
    'Title': dsFields.name, // Documents use "Title" instead of "Name"
    'Description': dsFields.description || { type: 'string', editable: 'text', filterable: true, sortable: false },
    'Folder': dsFields.folder,
    'Owner': dsFields.owner,
    'Tags': dsFields.tags,
    'Status': dsFields.status,
    'Last Update': dsFields.updated_at,
    // Library-specific columns
    'Identifiers': dsFields.identifiers,
    'Blueprint': dsFields.blueprint_id,
    'Blueprint Version': dsFields.blueprint_version,
    'Version': dsFields.version,
    'Type': dsFields.type,
    // Dialog-specific columns
    'Dialog Key': { type: 'string', editable: 'text', filterable: true, sortable: true },
    // Keep raw field names as well for direct access
    ...dsFields,
  };
};

// Cache the default metadata
const DEFAULT_FIELD_METADATA = getDefaultFieldMetadata();

interface MainTableWidgetProps {
  objectData: TableItem[];
  // Optional: Selected columns to display (if not provided, shows all columns from objectData)
  selectedColumns?: string[];
  // Optional: Raw items with full data for extracting field values
  rawItems?: any[];
  // Optional: Field metadata from data source for all columns
  fieldMetadata?: Record<string, FieldMetadata>;
  // Optional: Callback when cell value changes
  onCellValueChange?: (rowKey: string, cellKey: string, newValue: any) => void;
  // Optional: Row click handler - triggered on double click to open item
  onRowClick?: (rowKey: string) => void;
  // Pending changes props
  modifiedItems?: Set<string>;
  modifiedCells?: Map<string, string[]>;
  onSaveItem?: (itemId: string) => void;
  onDiscardItem?: (itemId: string) => void;
  // Optional: External selected rows state (for export functionality)
  selectedRows?: Set<string>;
  onSelectedRowsChange?: (selectedRows: Set<string>) => void;
}

const MainTableWidget: React.FC<MainTableWidgetProps> = ({ 
  objectData,
  selectedColumns,
  rawItems = [],
  fieldMetadata = {},
  onCellValueChange,
  onRowClick,
  modifiedItems = new Set(),
  modifiedCells = new Map(),
  onSaveItem,
  onDiscardItem,
  selectedRows: externalSelectedRows,
  onSelectedRowsChange,
}) => {
  const { t } = usePlatformTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const { selectedGame } = useSimplifiedUserData();

  // #region Collapsed Folders State
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string>>(new Set());
  
  const handleFolderToggle = useCallback((folderKey: string) => {
    setCollapsedFolders(prev => {
      const updated = new Set(prev);
      if (updated.has(folderKey)) {
        updated.delete(folderKey);
      } else {
        updated.add(folderKey);
      }
      return updated;
    });
  }, []);
  // #endregion

  // #region Data transformation for dynamic columns
  // Transform objectData to include selected columns (including field columns from rawItems)
  const transformedData = useMemo((): TableItem[] => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return objectData;
    }

    // Map metadata column IDs to their original keys in the data
    // Includes mappings for all item types (library, documents, blueprints, dialogs)
    const metadataKeyMap: Record<string, string> = {
      // Common across types
      'name': 'Name',
      'title': 'Title',
      'description': 'Description',
      'folder': 'Folder',
      'owner': 'Owner',
      'tags': 'Tags',
      'status': 'Status',
      'lastUpdate': 'Last Update',
      // Library-specific
      'identifiers': 'Identifiers',
      'blueprint': 'Blueprint',
      'blueprint_version': 'Blueprint Version',
      'version': 'Version',
      'type': 'Type',
      // Dialog-specific
      'dialog_key': 'Dialog Key',
    };

    // Reverse map: from display key to column ID
    const reverseMetadataKeyMap: Record<string, string> = {};
    Object.entries(metadataKeyMap).forEach(([id, displayKey]) => {
      reverseMetadataKeyMap[displayKey] = id;
    });

    // Get selected metadata columns
    const selectedMetadataKeys = selectedColumns
      .filter(col => !col.startsWith('field_'))
      .map(col => metadataKeyMap[col] || col);

    // Get selected field columns
    const selectedFieldIds = selectedColumns
      .filter(col => col.startsWith('field_'))
      .map(col => col.replace('field_', ''));

    // Helper function to sort content array according to selectedColumns order
    const sortContentBySelectedOrder = (content: any[]): any[] => {
      return content.sort((a, b) => {
        // Get the column ID for each content item
        const aColumnId = a.key.startsWith('field_') ? a.key : (reverseMetadataKeyMap[a.key] || a.key);
        const bColumnId = b.key.startsWith('field_') ? b.key : (reverseMetadataKeyMap[b.key] || b.key);
        
        // For field columns, the key is just the field ID, need to prepend 'field_'
        const aSelectedId = selectedMetadataKeys.includes(a.key) ? aColumnId : `field_${a.key}`;
        const bSelectedId = selectedMetadataKeys.includes(b.key) ? bColumnId : `field_${b.key}`;
        
        const aIndex = selectedColumns.indexOf(aSelectedId);
        const bIndex = selectedColumns.indexOf(bSelectedId);
        
        // If not found in selectedColumns, put at end
        const aPos = aIndex === -1 ? selectedColumns.length : aIndex;
        const bPos = bIndex === -1 ? selectedColumns.length : bIndex;
        
        return aPos - bPos;
      });
    };

    return objectData.map((item) => {
      if (item.type === 'row') {
        // Filter content to only include selected metadata columns
        const filteredContent = item.content.filter(cell => 
          selectedMetadataKeys.includes(cell.key)
        );

            // Add field columns from raw items - grouped by section
            if (selectedFieldIds.length > 0) {
              const rawItem = rawItems.find((ri: any) => ri.id === item.key);
              if (rawItem && Array.isArray(rawItem.data)) {
                // Build field columns sorted by section order
                const fieldColumns: Array<{
                  key: string;
                  label: string;
                  fixedX: boolean;
                  content: string;
                  sectionId: string;
                  sectionTitle: string;
                  sectionIndex: number;
                  fieldIndex: number;
                }> = [];
                
                rawItem.data.forEach((section: any, sectionIndex: number) => {
                  if (Array.isArray(section?.fields)) {
                    section.fields.forEach((field: any, fieldIndex: number) => {
                      if (selectedFieldIds.includes(field.id)) {
                        // Get field metadata to check if it's a linked field
                        const fieldKey = section.id ? `${section.id}_${field.id}` : field.id;
                        let fieldMeta = fieldMetadata[fieldKey] || fieldMetadata[field.id];
                        
                        // If metadata not found, derive it from field type
                        if (!fieldMeta && field.type) {
                          const linkedDataSource = mapFieldTypeToLinkedDataSource(field.type);
                          if (linkedDataSource) {
                            fieldMeta = {
                              type: 'string',
                              editable: 'dropdown',
                              filterable: true,
                              sortable: false,
                              linkedDataSource,
                              multiSelect: field.multiple === true,
                              displayField: 'name',
                              valueField: 'id',
                              // Pass filterOptions to restrict dropdown choices
                              options: field.filterOptions && Array.isArray(field.filterOptions) ? 
                                field.filterOptions.map((opt: string) => ({ label: opt, value: opt })) : 
                                undefined,
                            };
                            
                            // Store raw filterOptions for useFieldOptions hook
                            (fieldMeta as any).filterOptions = field.filterOptions;
                          }
                        }
                        
                        // Debug logging
                        console.log('[MainTableWidget] Processing field:', {
                          fieldId: field.id,
                          fieldType: field.type,
                          fieldKey,
                          hasMeta: !!fieldMeta,
                          linkedDataSource: fieldMeta?.linkedDataSource,
                          rawValue: field.value,
                        });
                        
                        // Resolve UUID to display name if field has linkedDataSource
                        let fieldValue = field.value || '';
                        if (fieldMeta?.linkedDataSource && fieldValue) {
                          console.log('[MainTableWidget] Resolving UUID:', {
                            fieldId: field.id,
                            linkedDataSource: fieldMeta.linkedDataSource,
                            rawValue: fieldValue,
                          });
                          fieldValue = resolveFieldDisplayValue(
                            fieldValue,
                            fieldMeta.linkedDataSource,
                            selectedGame?.id
                          );
                          console.log('[MainTableWidget] Resolved to:', fieldValue);
                        }
                        
                        // Handle array values (for display)
                        if (Array.isArray(fieldValue)) {
                          fieldValue = fieldValue.join(', ');
                        }
                        
                        fieldColumns.push({
                          key: field.id, // Use ID as unique key to avoid collision with metadata columns
                          label: field.label || field.id, // Store label for display
                          fixedX: false,
                          content: String(fieldValue),
                          sectionId: section.id || '',
                          sectionTitle: section.title || '',
                          sectionIndex,
                          fieldIndex,
                        });
                      }
                    });
                  }
                });
                
                // Add field columns (without the index properties)
                fieldColumns.forEach(({ key, label, fixedX, content, sectionId, sectionTitle }) => {
                  filteredContent.push({ key, label, fixedX, content, sectionId, sectionTitle });
                });
              }
            }

        // Sort the final content according to selectedColumns order
        const sortedContent = sortContentBySelectedOrder(filteredContent);

        return {
          ...item,
          content: sortedContent,
        };
      } else if (item.type === 'subfolder') {
        // Handle subfolder items
        return {
          ...item,
          items: item.items.map(subItem => {
            const filteredContent = subItem.content.filter(cell =>
              selectedMetadataKeys.includes(cell.key)
            );

            // Add field columns from raw items - grouped by section
            if (selectedFieldIds.length > 0) {
              const rawItem = rawItems.find((ri: any) => ri.id === subItem.key);
              if (rawItem && Array.isArray(rawItem.data)) {
                // Build field columns sorted by section order
                const fieldColumns: Array<{
                  key: string;
                  label: string;
                  fixedX: boolean;
                  content: string;
                  sectionId: string;
                  sectionTitle: string;
                  sectionIndex: number;
                  fieldIndex: number;
                }> = [];
                
                rawItem.data.forEach((section: any, sectionIndex: number) => {
                  if (Array.isArray(section?.fields)) {
                    section.fields.forEach((field: any, fieldIndex: number) => {
                      if (selectedFieldIds.includes(field.id)) {
                        // Get field metadata to check if it's a linked field
                        const fieldKey = section.id ? `${section.id}_${field.id}` : field.id;
                        let fieldMeta = fieldMetadata[fieldKey] || fieldMetadata[field.id];
                        
                        // If metadata not found, derive it from field type
                        if (!fieldMeta && field.type) {
                          const linkedDataSource = mapFieldTypeToLinkedDataSource(field.type);
                          if (linkedDataSource) {
                            fieldMeta = {
                              type: 'string',
                              editable: 'dropdown',
                              filterable: true,
                              sortable: false,
                              linkedDataSource,
                              multiSelect: field.multiple === true,
                              displayField: 'name',
                              valueField: 'id',
                              // Pass filterOptions to restrict dropdown choices
                              options: field.filterOptions && Array.isArray(field.filterOptions) ? 
                                field.filterOptions.map((opt: string) => ({ label: opt, value: opt })) : 
                                undefined,
                            };
                            
                            // Store raw filterOptions for useFieldOptions hook
                            (fieldMeta as any).filterOptions = field.filterOptions;
                          }
                        }
                        
                        // Resolve UUID to display name if field has linkedDataSource
                        let fieldValue = field.value || '';
                        if (fieldMeta?.linkedDataSource && fieldValue) {
                          fieldValue = resolveFieldDisplayValue(
                            fieldValue,
                            fieldMeta.linkedDataSource,
                            selectedGame?.id
                          );
                        }
                        
                        // Handle array values (for display)
                        if (Array.isArray(fieldValue)) {
                          fieldValue = fieldValue.join(', ');
                        }
                        
                        fieldColumns.push({
                          key: field.id, // Use ID as unique key
                          label: field.label || field.id, // Store label for display
                          fixedX: false,
                          content: String(fieldValue),
                          sectionId: section.id || '',
                          sectionTitle: section.title || '',
                          sectionIndex,
                          fieldIndex,
                        });
                      }
                    });
                  }
                });
                
                // Add field columns (without the index properties)
                fieldColumns.forEach(({ key, label, fixedX, content, sectionId, sectionTitle }) => {
                  filteredContent.push({ key, label, fixedX, content, sectionId, sectionTitle });
                });
              }
            }

            // Sort the final content according to selectedColumns order
            const sortedContent = sortContentBySelectedOrder(filteredContent);

            return {
              ...subItem,
              content: sortedContent,
            };
          }),
        };
      }
      return item;
    });
  }, [objectData, selectedColumns, rawItems]);
  // #endregion

  // #region Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageData = transformedData.slice((page - 1) * pageSize, page * pageSize);
  // #region Initiate
  // Extract column headers from the first data row (skip subfolder items)
  const firstDataRow = transformedData.find((item): item is TableDataRow => item.type === "row") 
    || (transformedData.find((item): item is TableSubfolder => item.type === "subfolder")?.items[0]);
  
  const columnHeaders = firstDataRow?.content.map((item) => ({
    key: item.key,
    label: (item as any).label,
    fixedX: item.fixedX,
    sectionId: (item as any).sectionId,
    sectionTitle: (item as any).sectionTitle,
  }));
  // Initialize column widths state - checkbox column (50px) + data columns (150px each)
  const [columnWidths, setColumnWidths] = useState<number[]>([
    50, // Checkbox column
    ...(columnHeaders?.map(() => 150) || []),
  ]);

  // Calculate and adjust column widths to fill available space
  useLayoutEffect(() => {
    const adjustColumnWidths = () => {
      if (!containerRef.current || !columnHeaders?.length) return;

      const availableWidth = containerRef.current.offsetWidth;
      const checkboxWidth = 50;
      const numDataColumns = columnHeaders.length;
      const minColumnWidth = 150;

      // Calculate total width with minimum widths
      const minTotalWidth = checkboxWidth + (numDataColumns * minColumnWidth);

      if (minTotalWidth < availableWidth) {
        // We have extra space to distribute among data columns
        const extraSpace = availableWidth - minTotalWidth;
        const additionalWidthPerColumn = extraSpace / numDataColumns;
        const newDataColumnWidth = minColumnWidth + additionalWidthPerColumn;

        setColumnWidths([
          checkboxWidth,
          ...Array(numDataColumns).fill(newDataColumnWidth)
        ]);
      } else {
        // Use minimum widths if available space is smaller
        setColumnWidths([
          checkboxWidth,
          ...Array(numDataColumns).fill(minColumnWidth)
        ]);
      }
    };

    // Adjust on mount and when window resizes
    adjustColumnWidths();
    window.addEventListener('resize', adjustColumnWidths);
    
    // Use ResizeObserver for container size changes
    const resizeObserver = new ResizeObserver(adjustColumnWidths);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', adjustColumnWidths);
      resizeObserver.disconnect();
    };
  }, [columnHeaders?.length]);
  // #endregion
  // #region Select
  const [internalSelectedRows, setInternalSelectedRows] = useState<Set<string>>(new Set());
  
  // Use external state if provided, otherwise use internal state
  const selectedRows = externalSelectedRows ?? internalSelectedRows;
  const setSelectedRows = onSelectedRowsChange ?? setInternalSelectedRows;

  const handleSelectAll = () => {
    // If all rows are selected, deselect all; otherwise, select all
    const allRowKeys = transformedData
      .filter(item => item.type === 'row')
      .map((row) => row.key);
    
    if (selectedRows.size === allRowKeys.length && allRowKeys.length > 0) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(allRowKeys));
    }
  };
  const handleRowSelect = (rowKey: string) => {
    const updated = new Set(selectedRows);
    if (updated.has(rowKey)) {
      updated.delete(rowKey);
    } else {
      updated.add(rowKey);
    }
    setSelectedRows(updated);
  };
  
  const allRowKeys = transformedData
    .filter(item => item.type === 'row')
    .map((row) => row.key);
  const allSelected = selectedRows.size === allRowKeys.length && allRowKeys.length > 0;

  // #endregion
  // #region Resize
  const handleColumnResize = (index: number, newWidth: number) => {
    setColumnWidths((prev) => {
      const updated = [...prev];
      // Minimum width already enforced in MT_ColumnHeader
      updated[index] = newWidth;
      return updated;
    });
  };
  // #endregion
  // #region Cell Editor
  const [cellEditor, setCellEditor] = useState<{
    isVisible: boolean;
    cellRect: DOMRect | null;
    value: any;
    cellWidth: number;
    rowKey: string;
    cellKey: string;
    editorType: EditableType;
    linkedDataSource?: LinkedDataSourceType;
    fieldMeta?: FieldMetadata;
  } | null>(null);

  // Use a ref to track the latest value for immediate access in save handler
  // This is needed because dropdown onChange + onSave happen in quick succession
  // and the state update may not have propagated yet
  const latestValueRef = useRef<any>(null);

  // Get field metadata for a cell key (with optional section ID for blueprint fields)
  const getFieldMetadataForCell = useCallback((cellKey: string, sectionId?: string): FieldMetadata | undefined => {
    // First try section-prefixed key for blueprint custom fields
    if (sectionId) {
      const sectionKey = `${sectionId}_${cellKey}`;
      if (fieldMetadata[sectionKey]) {
        return fieldMetadata[sectionKey];
      }
    }
    
    // Then check provided field metadata with just the cell key
    if (fieldMetadata[cellKey]) {
      return fieldMetadata[cellKey];
    }
    
    // Then check standard metadata column configs
    if (DEFAULT_FIELD_METADATA[cellKey]) {
      return DEFAULT_FIELD_METADATA[cellKey];
    }
    
    // Return undefined to allow derivation from raw data
    // (instead of returning default metadata which prevents derivation)
    return undefined;
  }, [fieldMetadata]);

  // Check if a field is editable
  const isFieldEditable = useCallback((cellKey: string, sectionId?: string): boolean => {
    const fieldMeta = getFieldMetadataForCell(cellKey, sectionId);
    // If no metadata found, assume it's editable (will be derived later)
    if (!fieldMeta) return true;
    return fieldMeta.editable !== false;
  }, [getFieldMetadataForCell]);

  // Get options for the current cell editor using the hook
  const { options: cellOptions, isLoading: isLoadingOptions } = useFieldOptions(
    cellEditor?.linkedDataSource,
    cellEditor?.fieldMeta
  );

  const handleCellEditStart = useCallback(
    (
      cellRect: DOMRect,
      value: any,
      cellWidth: number,
      rowKey: string,
      cellKey: string,
      sectionId?: string
    ) => {
      // Check if row is locked
      const rowData = objectData.find(item => {
        if (item.type === 'row') return item.key === rowKey;
        if (item.type === 'subfolder') {
          return item.items.some(row => row.key === rowKey);
        }
        return false;
      });
      
      let isRowLocked = false;
      if (rowData?.type === 'row') {
        isRowLocked = rowData.isLocked === true;
      } else if (rowData?.type === 'subfolder') {
        const row = rowData.items.find(r => r.key === rowKey);
        isRowLocked = row?.isLocked === true;
      }
      
      if (isRowLocked) {
        console.log('[MainTableWidget] Cannot edit locked row:', rowKey);
        return;
      }
      
      let fieldMeta = getFieldMetadataForCell(cellKey, sectionId);
      
      // If metadata not found, try to derive it from raw field data
      if (!fieldMeta && rawItems.length > 0) {
        const rawItem = rawItems.find((ri: any) => ri.id === rowKey);
        if (rawItem && Array.isArray(rawItem.data)) {
          for (const section of rawItem.data) {
            if (Array.isArray(section.fields)) {
              const field = section.fields.find((f: any) => f.id === cellKey);
              if (field) {
                if (field.type) {
                  const linkedDataSource = mapFieldTypeToLinkedDataSource(field.type);
                  
                  if (linkedDataSource) {
                    fieldMeta = {
                      type: 'string',
                      editable: 'dropdown',
                      filterable: true,
                      sortable: false,
                      linkedDataSource,
                      multiSelect: field.multiple === true,
                      displayField: 'name',
                      valueField: 'id',
                      // Do NOT set options here - let useFieldOptions fetch from stores
                      // filterOptions will be used by useFieldOptions to filter the results
                    };
                    
                    // Store raw filterOptions for useFieldOptions hook to use for filtering
                    (fieldMeta as any).filterOptions = field.filterOptions;
                  }
                }
                break;
              }
            }
          }
        }
      }
      
      // If field is explicitly marked as not editable, don't open editor
      if (fieldMeta && fieldMeta.editable === false) {
        return;
      }
      
      // If we still don't have metadata, use text as fallback
      if (!fieldMeta) {
        fieldMeta = { type: 'string', editable: 'text', filterable: true };
      }
      
      const editorType = fieldMeta.editable || 'text';
      const linkedDataSource = fieldMeta.linkedDataSource;
      
      // For linked fields with UUID values, we need to get the actual UUID back for editing
      // The displayed value is the resolved name, but we need to edit the UUID
      // Find the raw value from rawItems
      let editValue = value;
      if (linkedDataSource && rawItems.length > 0) {
        const rawItem = rawItems.find((ri: any) => ri.id === rowKey);
        if (rawItem && Array.isArray(rawItem.data)) {
          // Search through sections for this field
          for (const section of rawItem.data) {
            if (Array.isArray(section.fields)) {
              const field = section.fields.find((f: any) => f.id === cellKey);
              if (field) {
                editValue = field.value || '';
                
                // For multi-select fields, convert comma-separated strings to arrays
                if (fieldMeta?.multiSelect && typeof editValue === 'string' && editValue) {
                  editValue = editValue.includes(',') 
                    ? editValue.split(',').map(v => v.trim()).filter(Boolean)
                    : [editValue];
                }
                break;
              }
            }
          }
        }
      } else if (linkedDataSource) {
        // Handle standard metadata fields (tags, types, etc.) that might not be in rawItems.data
        // For multi-select fields, convert comma-separated strings to arrays
        if (fieldMeta?.multiSelect && typeof editValue === 'string' && editValue) {
          editValue = editValue.includes(',') 
            ? editValue.split(',').map(v => v.trim()).filter(Boolean)
            : [editValue];
        }
      }

      // Initialize the ref with the edit value (UUID for linked fields)
      latestValueRef.current = editValue;

      setCellEditor({
        isVisible: true,
        cellRect,
        value: editValue, // Use the raw UUID value for editing
        cellWidth,
        rowKey,
        cellKey,
        editorType,
        linkedDataSource,
        fieldMeta,
      });
    },
    [objectData, getFieldMetadataForCell, rawItems]
  );

  const handleCellEditorSave = useCallback(() => {
    if (!cellEditor) return;

    // Use the ref value (most up-to-date) instead of state value
    const { rowKey, cellKey } = cellEditor;
    const value = latestValueRef.current;
    
    console.log('='.repeat(60));
    console.log('[MainTableWidget] SAVING CELL');
    console.log('Row key:', rowKey);
    console.log('Cell key:', cellKey);
    console.log('Value from editor:', value);
    console.log('Value type:', typeof value);
    console.log('='.repeat(60));
    
    // Call the callback if provided
    if (onCellValueChange) {
      onCellValueChange(rowKey, cellKey, value);
    } else {
      console.warn('[MainTableWidget] No onCellValueChange callback provided');
    }
    
    setCellEditor(null);
    latestValueRef.current = null;
  }, [cellEditor, onCellValueChange]);

  const handleCellEditorCancel = useCallback(() => {
    setCellEditor(null);
    latestValueRef.current = null;
  }, []);

  // Handle boolean cell changes directly (no editor needed)
  const handleBooleanCellChange = useCallback((rowKey: string, cellKey: string, value: boolean) => {
    // Check if row is locked
    const rowData = objectData.find(item => {
      if (item.type === 'row') return item.key === rowKey;
      if (item.type === 'subfolder') {
        return item.items.some(row => row.key === rowKey);
      }
      return false;
    });
    
    let isRowLocked = false;
    if (rowData?.type === 'row') {
      isRowLocked = rowData.isLocked === true;
    } else if (rowData?.type === 'subfolder') {
      const row = rowData.items.find(r => r.key === rowKey);
      isRowLocked = row?.isLocked === true;
    }
    
    if (isRowLocked) {
      console.log('[MainTableWidget] Cannot edit locked row:', rowKey);
      return;
    }
    
    if (onCellValueChange) {
      onCellValueChange(rowKey, cellKey, value);
    }
  }, [objectData, onCellValueChange]);

  const handleCellEditorChange = useCallback((newValue: any) => {
    if (!cellEditor) return;

    console.log('[MainTableWidget] Cell value changed:', { cellKey: cellEditor.cellKey, newValue });

    // Update the ref immediately for synchronous access
    latestValueRef.current = newValue;

    setCellEditor((prev) =>
      prev
        ? {
            ...prev,
            value: newValue,
          }
        : null
    );
  }, [cellEditor]);
  // #endregion
  return (
    <TooltipProvider>
      <div className="bg-background flex flex-col">
        <div ref={containerRef} className="overflow-x-auto overflow-y-auto max-h-[600px]">
          <MT_ColumnHeader
            data={columnHeaders}
            columnWidths={columnWidths}
            onColumnResize={handleColumnResize}
            allSelected={allSelected}
            onSelectAll={handleSelectAll}
          />
          <MT_Datatable
            data={pageData}
            columnWidths={columnWidths}
            selectedRows={selectedRows}
            onRowSelect={handleRowSelect}
            collapsedFolders={collapsedFolders}
            onFolderToggle={handleFolderToggle}
            onCellEditStart={handleCellEditStart}
            onBooleanCellChange={handleBooleanCellChange}
            isFieldEditable={isFieldEditable}
            onRowClick={onRowClick}
            modifiedItems={modifiedItems}
            modifiedCells={modifiedCells}
            onSaveItem={onSaveItem}
            onDiscardItem={onDiscardItem}
          />
        </div>
        <MT_Pagination
          page={page}
          pageSize={pageSize}
          total={transformedData.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </div>

      {/* Cell Editor */}
      {cellEditor && (
        <CellEditor
          value={cellEditor.value}
          onChange={handleCellEditorChange}
          onSave={handleCellEditorSave}
          onCancel={handleCellEditorCancel}
          isVisible={cellEditor.isVisible}
          cellRect={cellEditor.cellRect}
          cellWidth={cellEditor.cellWidth}
          editorType={cellEditor.editorType}
          fieldMetadata={cellEditor.fieldMeta}
          options={cellOptions}
          isLoadingOptions={isLoadingOptions}
        />
      )}
    </TooltipProvider>
  );
};

export default MainTableWidget;
